'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { FileX2, Scale, Shield } from 'lucide-react';
import { useState } from 'react';
import { animDelay } from '../../utils/animDelay';
import { client, type RouterOutputs } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
type RuleRemoval = RouterOutputs['spaces']['removalStats']['ruleRemovalCounts'][number];
type RemovalStatsButtonProps = {
  spaceSlug: string;
};

/**
 * Banner entry point for the per-rule removal breakdown. Unlike the ban button
 * next to it, no total is rendered here: the count is a distinct-post tally
 * across every rule, too heavy to add to the space DTO that every space page
 * load fetches. The modal fetches it on open instead.
 */
const RemovalStatsButton = t0 => {
  const $ = _c(7);
  const {
    spaceSlug
  } = t0;
  const [modalOpen, setModalOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setModalOpen(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Button text="Removals" onClick={t1} variant="CLEAR" classNames="px-3 py-1" icon={<FileX2 size={16} />} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== modalOpen || $[3] !== spaceSlug) {
    t3 = modalOpen && <RemovalStatsModal closeHandler={() => setModalOpen(false)} spaceSlug={spaceSlug} />;
    $[2] = modalOpen;
    $[3] = spaceSlug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
type RemovalStatsModalProps = {
  closeHandler: () => void;
  spaceSlug: string;
};
const RemovalStatsModal = t0 => {
  const $ = _c(16);
  const {
    closeHandler,
    spaceSlug
  } = t0;
  let t1;
  if ($[0] !== spaceSlug) {
    t1 = {
      queryKey: ["removalStats", spaceSlug],
      queryFn: async () => {
        const [error, result] = await client.spaces.removalStats.query({
          slug: spaceSlug
        });
        if (error) {
          throw error;
        }
        return result;
      }
    };
    $[0] = spaceSlug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    isLoading
  } = useQuery(t1);
  let t2;
  if ($[2] !== data?.ruleRemovalCounts) {
    t2 = data?.ruleRemovalCounts ?? [];
    $[2] = data?.ruleRemovalCounts;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const ruleRemovalCounts = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      maxWidth: "800px",
      width: "90vw"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="anim-enter flex items-center gap-3 [--enter-y:-10px]"><FileX2 className="size-5 text-white" /><h2 className="text-2xl font-semibold text-textPrimary">Removal Statistics</h2></div>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== closeHandler) {
    t5 = <ModalHeader closeHandler={closeHandler}>{t4}</ModalHeader>;
    $[6] = closeHandler;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== data?.totalRemovals || $[9] !== isLoading || $[10] !== ruleRemovalCounts) {
    t6 = <ModalContent>{isLoading ? <div className="flex flex-col items-center justify-center p-12"><div className="size-12 animate-spin rounded-full border-4 border-primary/20 border-t-primary" /><p className="anim-enter mt-4 text-textSecondary [--enter-delay:500ms]">Loading removal statistics...</p></div> : <div className="anim-enter space-y-6"><div className="grid grid-cols-1 gap-4 md:grid-cols-2"><div className="anim-enter rounded-xl border border-ds-border-standard bg-background-secondary p-4 [--enter-ease:var(--ease-spring-soft)] [--enter-scale:0.9]"><div className="flex items-center gap-3"><div className="rounded-lg bg-primary/20 p-2"><FileX2 className="size-5 text-primary" /></div><div><p className="text-2xl font-semibold text-textPrimary">{data?.totalRemovals ?? 0}</p><p className="text-sm text-textSecondary">Posts Removed</p></div></div></div><div style={animDelay(100)} className="anim-enter rounded-xl border border-ds-border-standard bg-background-secondary p-4 [--enter-ease:var(--ease-spring-soft)] [--enter-scale:0.9]"><div className="flex items-center gap-3"><div className="rounded-lg bg-primary/20 p-2"><Scale className="size-5 text-primary" /></div><div><p className="text-2xl font-semibold text-textPrimary">{ruleRemovalCounts.length}</p><p className="text-sm text-textSecondary">Rules</p></div></div></div></div><div className="space-y-4"><h3 className="anim-enter flex items-center gap-2 text-lg font-semibold text-textPrimary [--enter-x:-20px]"><Shield className="size-5 text-primary" />Removals by Rule</h3>{ruleRemovalCounts.length > 0 ? <div className="space-y-3">{ruleRemovalCounts.map(_temp)}</div> : <div className="anim-enter py-12 text-center"><div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-500/10"><Shield className="size-8 text-green-500" /></div><h4 className="mb-2 text-lg font-emphasis text-textPrimary">No Rules Yet</h4><p className="text-textSecondary">This space has no rules, so no posts have been removed.</p></div>}</div></div>}</ModalContent>;
    $[8] = data?.totalRemovals;
    $[9] = isLoading;
    $[10] = ruleRemovalCounts;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== closeHandler || $[13] !== t5 || $[14] !== t6) {
    t7 = <Modal closeHandler={closeHandler} style={t3}>{t5}{t6}</Modal>;
    $[12] = closeHandler;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
type RuleRemovalRowProps = {
  item: RuleRemoval;
  index: number;
};
const RuleRemovalRow = t0 => {
  const $ = _c(20);
  const {
    item,
    index
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(_temp2);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = index + 1;
  let t3;
  if ($[1] !== item.rule.title || $[2] !== t2) {
    t3 = <p className="font-emphasis text-textPrimary">{t2}. {item.rule.title}</p>;
    $[1] = item.rule.title;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== item.removalCount) {
    t4 = <span className="text-lg font-semibold text-textPrimary">{item.removalCount}</span>;
    $[4] = item.removalCount;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = item.removalCount === 1 ? "post" : "posts";
  let t6;
  if ($[6] !== t5) {
    t6 = <span className="ml-1 text-xs text-textSecondary">{t5}</span>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = <div className="shrink-0 rounded-lg bg-primary/20 px-3 py-1 text-center">{t4}{t6}</div>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t3 || $[12] !== t7) {
    t8 = <div className="flex cursor-pointer items-center justify-between gap-4 p-4" onClick={t1}>{t3}{t7}</div>;
    $[11] = t3;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== item.rule.description || $[15] !== open) {
    t9 = open && <p className="border-t border-ds-border-standard px-4 py-3 text-sm text-textSecondary">{item.rule.description}</p>;
    $[14] = item.rule.description;
    $[15] = open;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <div className="rounded-xl border border-ds-border-standard bg-background-secondary">{t8}{t9}</div>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
export default RemovalStatsButton;
function _temp(item, index) {
  return <div key={item.rule.id} style={animDelay(index * 100)} className="anim-enter [--enter-y:20px]"><RuleRemovalRow index={index} item={item} /></div>;
}
function _temp2(x) {
  return !x;
}